// src/screens/superadmin/payouts/Payouts.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import { Row, Col } from 'react-bootstrap';
import {
  FaSearch,
  FaDollarSign,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaUniversity,
  FaStore,
  FaReceipt,
  FaInfoCircle,
  FaUpload,
  FaFileAlt
} from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import Button from '../../../components/general/button/Button';
import Pagination from '../../../components/general/pagination/Pagination';
import ActionDropdown from '../../../components/admin/dropdown/action/ActionDropdown';
import StatCard from '../../../components/admin/statcard/StatCard';
import '../../../assets/css/admin/payout/Payouts.css';
import AdminLayout from '../../../layouts/AdminLayout';
import MyContext from '../../../context/MyContext';

const Payouts = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);

  const [showViewModal, setShowViewModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  // Approve modal state
  const [receiptFile, setReceiptFile] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [payoutsList, setPayoutsList] = useState([]);
  const [totalPayouts, setTotalPayouts] = useState(0);
  const [pendingPayouts, setPendingPayouts] = useState(0);
  const [completedPayouts, setCompletedPayouts] = useState(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Processing', label: 'Processing' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Failed', label: 'Rejected' }
  ];

  // ── viewAllPayouts ─────────────────────────────────────────────
  const viewAllPayouts = () => {
    setLoading(true);
    axios
      .post(`${baseUrl}/admin/payout/viewAllPayouts`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        console.log("Payouts response:", response.data);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else {
          setPayoutsList(response.data.payouts || []);
          setTotalPayouts(response.data.totalPayouts || 0);
          setPendingPayouts(response.data.pendingPayouts || 0);
          setCompletedPayouts(response.data.completedPayouts || 0);
          setTotalPaidAmount(response.data.totalPaidAmount || 0);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load payouts." });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllPayouts();
  }, [authToken]);

  // ── 1000 se upar amounts ko "1.5K" mein dikhata hai, warna full number ──
  const formatAmount = (amount) => {
    const value = Number(amount) || 0;
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return value.toLocaleString();
  };

  const statsData = [
    { icon: FaDollarSign, iconBg: 'linear-gradient(135deg, #e3f2fd, #bbdefb)', iconColor: '#2196f3', label: 'Total Payouts', value: (totalPayouts || 0).toString(), badge: 'All' },
    { icon: FaCheckCircle, iconBg: 'linear-gradient(135deg, #e8f5f0, #d4ede3)', iconColor: '#0ac46c', label: 'Completed', value: (completedPayouts || 0).toString(), badge: 'Paid' },
    { icon: FaClock, iconBg: 'linear-gradient(135deg, #fff3e0, #ffe0b2)', iconColor: '#ff9800', label: 'Pending', value: (pendingPayouts || 0).toString(), badge: 'Queue' },
    { icon: FaUniversity, iconBg: 'linear-gradient(135deg, #f3e5f5, #e1bee7)', iconColor: '#9c27b0', label: 'Total Paid', value: `Rs. ${formatAmount(totalPaidAmount)}`, badge: 'Success' }
  ];

  const handleView = (payout) => {
    setSelectedPayout(payout);
    setShowViewModal(true);
  };

  const handleApprove = (payout) => {
    setSelectedPayout(payout);
    setShowApproveModal(true);
    setReceiptFile(null);
    setApprovalNotes('');
  };

  // ── approvePayout (via updatePayoutStatus + receipt) ─────────────
  const confirmApprove = () => {
    if (!receiptFile) {
      Swal.fire({ icon: "warning", title: "Missing Receipt", text: "Please upload transfer receipt!" });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append("id", selectedPayout.id);
    formData.append("status", "Completed");
    formData.append("recieptImage", receiptFile);

    axios
      .post(`${baseUrl}/admin/payout/updatePayoutStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setSubmitting(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        } else if (response.data.success == true) {
          Swal.fire({ icon: "success", title: "Success", text: "Payout marked as paid successfully!" });
          setShowApproveModal(false);
          setShowViewModal(false);
          viewAllPayouts();
        } else {
          Swal.fire({ icon: "error", title: "Error", text: response.data.message });
        }
      })
      .catch((error) => {
        setSubmitting(false);
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to approve payout." });
      });
  };

  // ── rejectPayout (via updatePayoutStatus) ─────────────────────
  const rejectPayout = (payout) => {
    Swal.fire({
      title: 'Reject this payout?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, reject it',
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", payout.id);
        formData.append("status", "Failed");

        axios
          .post(`${baseUrl}/admin/payout/updatePayoutStatus`, formData, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            if (response.data.hasOwnProperty("error")) {
              Swal.fire({ icon: "error", title: "Error", text: response.data.error });
            } else if (response.data.success == true) {
              Swal.fire({ icon: "success", title: "Rejected", text: "Payout has been rejected." });
              setShowViewModal(false);
              viewAllPayouts();
            } else {
              Swal.fire({ icon: "error", title: "Error", text: response.data.message });
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({ icon: "error", title: "Error", text: "Failed to reject payout." });
          });
      }
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) { // 5MB limit
        Swal.fire({ icon: "warning", title: "File too large", text: "File size should be less than 5MB" });
        return;
      }
      setReceiptFile(file);
    }
  };

  const filteredPayouts = payoutsList.filter(payout => {
    const matchesSearch =
      String(payout.id).includes(searchTerm.toLowerCase()) ||
      (payout.kmseller?.name || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || payout.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getActions = (payout) => {
    const actions = [
      {
        icon: 'MdVisibility',
        label: 'View Details',
        onClick: () => handleView(payout)
      }
    ];

    if (payout.status === 'Pending') {
      actions.push(
        {
          icon: 'MdCheckCircle',
          label: 'Mark as Paid',
          onClick: () => handleApprove(payout)
        },
        {
          icon: 'MdCancel',
          label: 'Reject',
          onClick: () => rejectPayout(payout)
        }
      );
    }

    if (payout.status === 'Completed' && !payout.recieptImage) {
      actions.push({
        icon: 'MdUpload',
        label: 'Upload Receipt',
        onClick: () => handleApprove(payout)
      });
    }

    return actions;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Pending': return 'ksa-payout-badge-pending';
      case 'Processing': return 'ksa-payout-badge-processing';
      case 'Completed': return 'ksa-payout-badge-completed';
      case 'Failed': return 'ksa-payout-badge-rejected';
      default: return 'ksa-payout-badge-default';
    }
  };

  const getStatusLabel = (status) => {
    return status === 'Failed' ? 'Rejected' : status;
  };

  // ── raw ISO timestamp ko "12 Jul 2026" mein trim karta hai ──
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <AdminLayout>
    <div className="ksa-payouts-container">
      {/* Page Header */}
      <div className="ksa-payout-page-header">
        <div>
          <h1 className="ksa-layout-page-title">Payout Management</h1>
          <p className="ksa-layout-page-subtitle">Manage seller withdrawal requests</p>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="ksa-payout-stats g-3 mb-4">
        {loading ? (
          Array(4).fill(0).map((_, idx) => (
            <Col key={idx} xs={12} sm={6} lg={3}>
              <div className="ksa-stat-card ksa-skeleton-stat">
                <div className="ksa-skeleton-content">
                  <div className="ksa-skeleton-line ksa-skeleton-label"></div>
                  <div className="ksa-skeleton-line ksa-skeleton-value"></div>
                </div>
                <div className="ksa-skeleton-icon"></div>
              </div>
            </Col>
          ))
        ) : (
          statsData.map((stat, idx) => (
            <Col key={idx} xs={12} sm={6} lg={3}>
              <StatCard {...stat} />
            </Col>
          ))
        )}
      </Row>

      {/* Main Table Card */}
      <div className="ksa-card">
        {/* Filters and Search */}
        <div className="ksa-payout-table-header">
          <div className="ksa-payout-filters">
            <select
              className="ksa-payout-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="ksa-payout-search">
            <FaSearch size={16} />
            <input
              type="text"
              placeholder="Search by payout ID or seller..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="ksa-payout-table-wrapper">
          <table className="ksa-payout-table">
            <thead>
              <tr>
                <th>PAYOUT #</th>
                <th>SELLER</th>
                <th>BANK ACCOUNT</th>
                <th>AMOUNT</th>
                <th>REQUEST DATE</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5).fill(0).map((_, idx) => (
                  <tr key={idx}>
                    <td><div className="ksa-skeleton-line" style={{width: '120px'}}></div></td>
                    <td>
                      <div className="ksa-skeleton-line" style={{width: '180px', marginBottom: '6px'}}></div>
                      <div className="ksa-skeleton-line" style={{width: '140px'}}></div>
                    </td>
                    <td><div className="ksa-skeleton-line" style={{width: '140px'}}></div></td>
                    <td><div className="ksa-skeleton-line" style={{width: '100px'}}></div></td>
                    <td><div className="ksa-skeleton-line" style={{width: '90px'}}></div></td>
                    <td><div className="ksa-skeleton-badge"></div></td>
                    <td><div className="ksa-skeleton-actions"></div></td>
                  </tr>
                ))
              ) : filteredPayouts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    No payout requests found
                  </td>
                </tr>
              ) : (
                filteredPayouts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((payout) => (
                  <tr key={payout.id}>
                    <td className="ksa-payout-number">PAY-{payout.id}</td>
                    <td className="ksa-payout-seller">
                      <div className="ksa-seller-info">
                        <span className="ksa-seller-name">{payout.kmseller?.name || "N/A"}</span>
                        <span className="ksa-seller-email">{payout.kmseller?.email || ""}</span>
                      </div>
                    </td>
                    <td className="ksa-payout-period">
                      {payout.kmsellerbank
                        ? `${payout.kmsellerbank.bankname} - ${payout.kmsellerbank.accountnumber}`
                        : "N/A"}
                    </td>
                    <td className="ksa-payout-amount">Rs. {formatAmount(payout.amount)}</td>
                    <td className="ksa-payout-date">{formatDate(payout.created_at)}</td>
                    <td>
                      <span className={`ksa-payout-status-badge ${getStatusBadgeClass(payout.status)}`}>
                        {getStatusLabel(payout.status)}
                      </span>
                    </td>
                    <td>
                      <ActionDropdown actions={getActions(payout)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          totalItems={filteredPayouts.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      {/* View Details Modal */}
      {showViewModal && selectedPayout && (
        <div className="ksa-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="ksa-modal ksa-modal-lg ksa-payout-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ksa-payout-modal-header">
              <h3 className="ksa-payout-modal-title">Payout Request Details</h3>
              <button className="ksa-modal-close" onClick={() => setShowViewModal(false)}>
                <FaTimes size={18} />
              </button>
            </div>

            <div className="ksa-payout-modal-body">
              <Row className="g-4">
                <Col xs={12}>
                  <div className="ksa-payout-info-card">
                    <h5 className="ksa-payout-section-title">
                      <FaInfoCircle style={{marginRight: '8px'}} />
                      Payout Information
                    </h5>
                    <div className="ksa-payout-info-grid">
                      <div className="ksa-payout-info-item">
                        <label>Payout Number</label>
                        <p>PAY-{selectedPayout.id}</p>
                      </div>
                      <div className="ksa-payout-info-item">
                        <label>Request Date</label>
                        <p>{formatDate(selectedPayout.created_at)}</p>
                      </div>
                      <div className="ksa-payout-info-item">
                        <label>Status</label>
                        <span className={`ksa-payout-status-badge ${getStatusBadgeClass(selectedPayout.status)}`}>
                          {getStatusLabel(selectedPayout.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="ksa-payout-info-card">
                    <h5 className="ksa-payout-section-title">
                      <FaStore style={{marginRight: '8px'}} />
                      Seller Information
                    </h5>
                    <div className="ksa-payout-details-list">
                      <div className="ksa-payout-detail-item">
                        <label>Seller Name</label>
                        <p>{selectedPayout.kmseller?.name || "N/A"}</p>
                      </div>
                      <div className="ksa-payout-detail-item">
                        <label>Email</label>
                        <p>{selectedPayout.kmseller?.email || "N/A"}</p>
                      </div>
                      <div className="ksa-payout-detail-item">
                        <label>Phone</label>
                        <p>{selectedPayout.kmseller?.phone || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="ksa-payout-info-card">
                    <h5 className="ksa-payout-section-title">
                      <FaUniversity style={{marginRight: '8px'}} />
                      Bank Details
                    </h5>
                    <div className="ksa-payout-details-list">
                      <div className="ksa-payout-detail-item">
                        <label>Bank Name</label>
                        <p>{selectedPayout.kmsellerbank?.bankname || "N/A"}</p>
                      </div>
                      <div className="ksa-payout-detail-item">
                        <label>Account Number</label>
                        <p>{selectedPayout.kmsellerbank?.accountnumber || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col xs={12}>
                  <div className="ksa-payout-info-card ksa-payout-amount-card">
                    <h5 className="ksa-payout-section-title">
                      <FaDollarSign style={{marginRight: '8px'}} />
                      Amount
                    </h5>
                    <div className="ksa-payout-amount-breakdown">
                      <div className="ksa-payout-amount-row ksa-payout-net-row">
                        <span className="ksa-payout-amount-label">Withdrawal Amount</span>
                        <span className="ksa-payout-amount-value ksa-net-amount">Rs. {formatAmount(selectedPayout.amount)}</span>
                      </div>
                    </div>
                  </div>
                </Col>

                {selectedPayout.status === 'Completed' && selectedPayout.recieptImage && (
                  <Col xs={12}>
                    <div className="ksa-payout-info-card">
                      <h5 className="ksa-payout-section-title">
                        <FaReceipt style={{marginRight: '8px'}} />
                        Transfer Receipt
                      </h5>
                      <div className="ksa-payout-receipt-view">
                        <a
                          href={`${baseUrlForFiles}/${selectedPayout.recieptImage}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ksa-payout-receipt-link"
                        >
                          <FaFileAlt /> View Receipt
                        </a>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>

            {selectedPayout.status === 'Pending' && (
              <div className="ksa-payout-modal-footer">
                <button
                  className="ksa-payout-btn-reject"
                  onClick={() => rejectPayout(selectedPayout)}
                >
                  <FaTimes /> Reject
                </button>
                <button
                  className="ksa-payout-btn-approve"
                  onClick={() => {
                    setShowViewModal(false);
                    handleApprove(selectedPayout);
                  }}
                >
                  <FaCheckCircle /> Mark as Paid
                </button>
              </div>
            )}

            {selectedPayout.status === 'Completed' && !selectedPayout.recieptImage && (
              <div className="ksa-payout-modal-footer">
                <button
                  className="ksa-payout-btn-approve"
                  onClick={() => {
                    setShowViewModal(false);
                    handleApprove(selectedPayout);
                  }}
                >
                  <FaUpload /> Upload Receipt
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approve Modal with Receipt Upload */}
      {showApproveModal && selectedPayout && (
        <div className="ksa-modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="ksa-modal ksa-modal-md ksa-payout-approve-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ksa-payout-modal-header">
              <h3 className="ksa-payout-modal-title">
                {selectedPayout.recieptImage ? 'Upload Receipt' : 'Mark as Paid'}
              </h3>
              <button className="ksa-modal-close" onClick={() => setShowApproveModal(false)}>
                <FaTimes size={18} />
              </button>
            </div>

            <div className="ksa-payout-modal-body">
              <div className="ksa-payout-approve-summary">
                <p><strong>Payout Number:</strong> PAY-{selectedPayout.id}</p>
                <p><strong>Seller:</strong> {selectedPayout.kmseller?.name || "N/A"}</p>
                <p><strong>Amount:</strong> <span className="ksa-approve-amount">Rs. {formatAmount(selectedPayout.amount)}</span></p>
              </div>

              <div className="ksa-payout-upload-section">
                <label className="ksa-payout-upload-label">
                  <FaUpload /> Upload Transfer Receipt *
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="ksa-payout-file-input"
                />
                {receiptFile && (
                  <div className="ksa-payout-file-selected">
                    <FaFileAlt /> {receiptFile.name}
                  </div>
                )}
                <small className="ksa-payout-file-hint">Accepted formats: PDF, JPG, PNG (Max 5MB)</small>
              </div>
            </div>

            <div className="ksa-payout-modal-footer">
              <button
                className="ksa-payout-btn-cancel"
                onClick={() => setShowApproveModal(false)}
              >
                Cancel
              </button>
              <button
                className="ksa-payout-btn-confirm"
                onClick={confirmApprove}
                disabled={submitting}
              >
                <FaCheckCircle /> Mark as Paid
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default Payouts;
